import api from "../../../shared/services/api";

const BASE = (conversaId) => `/whatsapp/meta/conversas/${conversaId}/mensagens`;

/**
 * Lista mensagens de uma conversa (mais recentes primeiro).
 * Paginação keyset via before_id.
 * @param {number} conversaId
 * @param {{ limit?: number, beforeId?: number|null }} opts
 * @returns {{ mensagens: Array, hasMore: boolean }}
 */
export async function listarMensagens(conversaId, { limit = 30, beforeId = null } = {}) {
  const params = { limit };
  if (beforeId) params.before_id = beforeId;

  const resp = await api.get(BASE(conversaId), { params });
  const data = resp.data?.data ?? resp.data ?? {};
  return {
    mensagens: data.mensagens ?? data.data ?? [],
    hasMore:   data.hasMore   ?? false,
  };
}

/**
 * Envia mensagem de texto livre.
 * Apenas dentro da janela de 24h.
 * @param {number} conversaId
 * @param {string} texto
 */
export async function enviarTexto(conversaId, texto) {
  const resp = await api.post(BASE(conversaId), {
    tipo:    "text",
    payload: { text: texto },
  });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia mensagem com mídia (image/video/audio/document).
 * Requer media_id obtido via midiaApi.upload() previamente.
 * @param {number} conversaId
 * @param {{ tipo: string, mediaId: string, caption?: string, filename?: string }} opts
 */
export async function enviarMidia(conversaId, { tipo, mediaId, caption = null, filename = null }) {
  const payload = { media_id: mediaId };
  if (caption)  payload.caption  = caption;
  if (filename) payload.filename = filename;

  const resp = await api.post(BASE(conversaId), { tipo, payload });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia template aprovado.
 * @param {number} conversaId
 * @param {{ templateName: string, languageCode: string, components: Array }} opts
 */
export async function enviarTemplate(conversaId, { templateName, languageCode, components = [] }) {
  const resp = await api.post(BASE(conversaId), {
    tipo:    "template",
    payload: {
      template_name:     templateName,
      language:          languageCode,
      components_params: components,
    },
  });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia localização.
 * @param {number} conversaId
 * @param {{ latitude: number, longitude: number, name?: string, address?: string }} opts
 */
export async function enviarLocalizacao(conversaId, { latitude, longitude, name = null, address = null }) {
  const payload = { latitude, longitude };
  if (name)    payload.name    = name;
  if (address) payload.address = address;

  const resp = await api.post(BASE(conversaId), { tipo: "location", payload });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia contatos (vCards).
 * @param {number} conversaId
 * @param {Array} contacts — array no formato Meta contacts[]
 */
export async function enviarContatos(conversaId, contacts) {
  const resp = await api.post(BASE(conversaId), {
    tipo:    "contacts",
    payload: { contacts },
  });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia mensagem interativa (botões reply ou lista).
 * @param {number} conversaId
 * @param {{ interactive_type: 'button'|'list', body: object, footer?: object, header?: object, action: object }} opts
 */
export async function enviarInterativo(conversaId, { interactive_type, body, footer, header, action }) {
  const interactive = { type: interactive_type, body, action };
  if (footer) interactive.footer = footer;
  if (header) interactive.header = header;

  const resp = await api.post(BASE(conversaId), {
    tipo:    "interactive",
    payload: { interactive },
  });
  return resp.data?.data ?? resp.data;
}

/**
 * Envia reação a uma mensagem.
 * Usa o atalho POST /reacao.
 * Para remover reação, passe emoji = "".
 * @param {number} conversaId
 * @param {string} wamid  — wamid da mensagem alvo
 * @param {string} emoji  — emoji Unicode ou "" para remover
 */
export async function enviarReacao(conversaId, wamid, emoji) {
  const resp = await api.post(
    `/whatsapp/meta/conversas/${conversaId}/reacao`,
    { wamid, emoji },
  );
  return resp.data?.data ?? resp.data;
}

/**
 * Traduz uma mensagem inbound para pt-BR via OpenAI (cache no backend).
 * Detecta idioma + traduz numa única chamada. Se idioma_detectado === 'pt'
 * retorna texto_traduzido = null (UI esconde o botão).
 * @param {number} mensagemId
 * @returns {Promise<{ id, texto_traduzido: string|null, idioma_detectado: string|null, cached: boolean }>}
 */
export async function traduzirMensagem(mensagemId) {
  const resp = await api.post(`/whatsapp/meta/mensagens/${mensagemId}/traduzir`);
  return resp.data?.data ?? resp.data;
}
